import { useEffect, useId } from 'react';
import { createPortal } from 'react-dom';
import { classNames as cn } from 'primereact/utils';
import { Button, Checkbox, CloseIcon, DownloadIcon, EyeToSeeIcon, LoadingSpinner } from '@repo/atomic-components';
import type { CheckboxChangeEvent } from '@repo/atomic-components';
import { ChatDocument } from '@/store';
import { useFocusTrap } from '@/hooks';
import { formatChatDocumentMetadata } from '../utils/formatChatDocumentMetadata';

export interface ChatDownloadFilesDialogProps {
  visible: boolean;
  onHide: () => void;
  sortedFiles: ChatDocument[];
  selectedIds: ReadonlySet<string>;
  allSelectableSelected: boolean;
  selectAllLabel: string;
  selectedSelectableCount: number;
  isBulkDownloading: boolean;
  isFullChatDownloading: boolean;
  isLoadingDocuments: boolean;
  onToggleSelectAll: (event: CheckboxChangeEvent) => void;
  onToggleOne: (documentId: string, checked: boolean) => void;
  onDownloadSelected: () => void | Promise<void>;
  onDownloadFullChat: () => void | Promise<void>;
  onDownloadSingle: (doc: ChatDocument) => void | Promise<void>;
  onViewSingle: (doc: ChatDocument) => void | Promise<void>;
  citedCaseFiles: ChatDocument[];
}

interface DocumentRowProps {
  doc: ChatDocument;
  selectable: boolean;
  selected?: boolean;
  onToggle?: (documentId: string, checked: boolean) => void;
  onDownload: (doc: ChatDocument) => void | Promise<void>;
  onView: (doc: ChatDocument) => void | Promise<void>;
}

const DocumentRow = ({ doc, selectable, selected = false, onToggle, onDownload, onView }: DocumentRowProps) => {
  const metadataLine = formatChatDocumentMetadata(doc);

  return (
    <div
      className={cn(
        'border-theme-border-grey bg-theme-white group relative flex w-full items-center gap-3 rounded-lg border p-4',
      )}
    >
      {selectable && (
        <div onClick={(event) => event.stopPropagation()} className="flex items-center justify-center">
          <Checkbox
            type="checkbox"
            className="case-list-filter-checkbox !text-theme-black !border-theme-text-grey"
            checked={selected}
            onChange={(event) => onToggle?.(doc.documentId, Boolean(event.checked))}
          />
        </div>
      )}

      <div className="min-w-0 flex-1">
        <div className="text-theme-black truncate text-sm font-semibold" title={doc.documentName}>
          {doc.documentName}
        </div>

        {metadataLine ? (
          <div className="text-theme-text-grey mt-1 truncate text-xs" title={metadataLine}>
            {metadataLine}
          </div>
        ) : null}
      </div>

      <div className="flex items-center gap-[12px]">
        <Button
          type="button"
          outlined
          className="flex w-[16px] shrink-0 items-center justify-center border-0 p-0 opacity-100 transition-opacity hover:bg-transparent md:opacity-0 md:group-focus-within:opacity-100 md:group-hover:opacity-100"
          aria-label={`View ${doc.documentName}`}
          onClick={() => void onView(doc)}
        >
          <EyeToSeeIcon width={16} height={16} className="text-theme-text-grey" />
        </Button>

        <Button
          type="button"
          outlined
          className="border-theme-border-grey hover:bg-theme-background-grey! flex h-[32px] w-[32px] shrink-0 items-center justify-center p-0 opacity-100 transition-opacity md:opacity-0 md:group-focus-within:opacity-100 md:group-hover:opacity-100"
          aria-label={`Download ${doc.documentName}`}
          onClick={() => void onDownload(doc)}
        >
          <DownloadIcon width={16} height={16} className="text-theme-text-grey" />
        </Button>
      </div>
    </div>
  );
};

export const ChatDownloadFilesDialog = ({
  visible,
  onHide,
  sortedFiles,
  selectedIds,
  allSelectableSelected,
  selectAllLabel,
  selectedSelectableCount,
  isBulkDownloading,
  isFullChatDownloading,
  isLoadingDocuments,
  onToggleSelectAll,
  onToggleOne,
  onDownloadSelected,
  onDownloadFullChat,
  onDownloadSingle,
  onViewSingle,
  citedCaseFiles,
}: ChatDownloadFilesDialogProps) => {
  const titleId = useId();
  const setDialogRef = useFocusTrap<HTMLDivElement>({
    active: visible,
  });

  useEffect(() => {
    if (!visible) {
      return;
    }

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [visible]);

  useEffect(() => {
    if (!visible) {
      return;
    }

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        onHide();
      }
    };

    window.addEventListener('keydown', onKeyDown);

    return () => window.removeEventListener('keydown', onKeyDown);
  }, [visible, onHide]);

  if (!visible) {
    return null;
  }

  const hasUploadedFiles = sortedFiles.length > 0;
  const hasCitedFiles = citedCaseFiles.length > 0;
  const hasDocuments = hasUploadedFiles || hasCitedFiles;

  return createPortal(
    <div
      className="fixed inset-0 z-[1100] flex items-center justify-center bg-black/40 p-[max(1rem,2vw)]"
      role="presentation"
      onClick={onHide}
    >
      <div
        ref={setDialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        className="border-theme-border-grey bg-theme-white flex max-h-[min(47.8125rem,100%)] min-h-[min(20rem,100%)] w-200 max-w-full min-w-0 flex-col rounded-lg border shadow-lg outline-none"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="border-theme-border-grey flex h-18 w-full shrink-0 items-center justify-between border-b px-6 py-[21px]">
          <h2 id={titleId} className="text-theme-black flex h-15 items-center justify-center text-lg font-semibold">
            Chat documents
          </h2>

          <Button
            type="button"
            onClick={onHide}
            outlined
            aria-label="Close"
            className="text-theme-black -mr-2 inline-flex h-[30px] w-[30px] shrink-0 cursor-pointer items-center justify-center rounded-md border-0 transition-colors"
          >
            <CloseIcon width={16} height={16} />
          </Button>
        </div>

        <div className="relative flex min-h-0 flex-1 flex-col">
          <div className="border-theme-border-grey bg-theme-background-grey flex min-h-[49px] shrink-0 flex-wrap items-center gap-4 border-b px-6 py-2">
            <div className="flex items-center gap-4">
              <label className="text-theme-black flex min-w-29.25 cursor-pointer items-center gap-2 text-sm font-medium select-none">
                <Checkbox
                  type="checkbox"
                  className="case-list-filter-checkbox !text-theme-black !border-theme-text-grey"
                  checked={allSelectableSelected}
                  onChange={onToggleSelectAll}
                  disabled={sortedFiles.length === 0}
                />
                <span>{selectAllLabel}</span>
              </label>

              <span className="text-theme-text-grey text-sm">
                {selectedSelectableCount} of {sortedFiles.length} selected
              </span>
            </div>

            <div className="ml-auto flex flex-wrap items-center justify-end gap-2">
              <Button
                type="button"
                outlined
                disabled={selectedSelectableCount === 0 || isBulkDownloading}
                className="border-theme-border-grey hover:bg-theme-background-grey! text-theme-black shrink-0 gap-2"
                onClick={() => void onDownloadSelected()}
              >
                {isBulkDownloading ? (
                  <LoadingSpinner size={16} className="!w-fit" />
                ) : (
                  <DownloadIcon width={16} height={16} />
                )}
                Download selected document(s)
              </Button>

              <Button
                type="button"
                outlined
                disabled={isFullChatDownloading}
                loading={isFullChatDownloading}
                className="border-theme-border-grey hover:bg-theme-background-grey! text-theme-black shrink-0 gap-2"
                icon={<DownloadIcon width={16} height={16} />}
                label="Download entire chat"
                onClick={() => void onDownloadFullChat()}
              />
            </div>
          </div>

          <div className="flex min-h-0 min-w-0 flex-1 flex-col gap-6 overflow-y-auto px-6 pt-4 pb-6">
            {!hasDocuments ? (
              <div className="flex min-h-0 min-w-0 flex-1 flex-col items-center justify-center gap-3" role="status">
                {isLoadingDocuments ? (
                  <>
                    <LoadingSpinner size={32} className="!h-fit" />
                    <p className="text-theme-black text-center">Loading documents...</p>
                  </>
                ) : (
                  <p className="text-theme-black text-center">No documents in chat</p>
                )}
              </div>
            ) : (
              <>
                {hasUploadedFiles && (
                  <section aria-labelledby="uploaded-files-heading" className="flex flex-col gap-3">
                    <h3 id="uploaded-files-heading" className="text-theme-black text-sm font-semibold">
                      Uploaded files
                    </h3>

                    {sortedFiles.map((doc) => (
                      <DocumentRow
                        key={doc.documentId}
                        doc={doc}
                        selectable
                        selected={selectedIds.has(doc.documentId)}
                        onToggle={onToggleOne}
                        onView={onViewSingle}
                        onDownload={onDownloadSingle}
                      />
                    ))}
                  </section>
                )}

                {hasCitedFiles && (
                  <section aria-labelledby="cited-files-heading" className="flex flex-col gap-3">
                    <h3 id="cited-files-heading" className="text-theme-black text-sm font-semibold">
                      Case files cited
                    </h3>

                    {citedCaseFiles.map((doc) => (
                      <DocumentRow
                        key={doc.documentId}
                        doc={doc}
                        selectable={false}
                        onView={onViewSingle}
                        onDownload={onDownloadSingle}
                      />
                    ))}
                  </section>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
};
